import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  fetchBudgetInputPrices,
  massUpdateBudgetInputPrices,
} from './bulkEditService';
import {
  applyFixedPriceToIds,
  applyPercentageToIds,
  buildMassUpdatePayload,
  clearEditedValuesForIds,
  getInvalidEditedIds,
  getModifiedIds,
  setEditedValue,
} from './bulkEditState';
import {
  BudgetInputPrice,
  BulkEditInputsFilters,
  EditedValues,
  LoadStatus,
} from './types';

const DEFAULT_PER_PAGE = 25;

const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const errorWithResponse = error as {
    response?: {
      data?: {
        message?: string;
      };
    };
  };

  return errorWithResponse?.response?.data?.message || fallbackMessage;
};

type UseBulkEditInputsParams = {
  budgetId: number;
};

type UseBulkEditInputsResult = {
  status: LoadStatus;
  inputs: BudgetInputPrice[];
  page: number;
  perPage: number;
  lastPage: number;
  total: number;
  sort: string | null;
  selectedIds: number[];
  editedValues: EditedValues;
  modifiedIds: number[];
  invalidModifiedIds: number[];
  hasUnsavedChanges: boolean;
  hasValidChanges: boolean;
  isSaving: boolean;
  loadErrorMessage: string | null;
  saveErrorMessage: string | null;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  updateSort: (sort: string) => void;
  applyFilters: (filters: BulkEditInputsFilters) => void;
  reload: () => Promise<void>;
  toggleSelection: (id: number) => void;
  toggleSelectAllCurrentPage: () => void;
  clearSelection: () => void;
  editUnitPrice: (
    item: BudgetInputPrice,
    value: number | null | undefined,
  ) => void;
  undoItemChanges: (id: number) => void;
  undoSelectedChanges: () => void;
  applyFixedPriceToSelection: (value: number | null | undefined) => void;
  applyPercentageToSelection: (percentage: number | null | undefined) => void;
  saveChanges: () => Promise<boolean>;
};

export const useBulkEditInputs = ({
  budgetId,
}: UseBulkEditInputsParams): UseBulkEditInputsResult => {
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [inputs, setInputs] = useState<BudgetInputPrice[]>([]);

  const [page, setPageState] = useState(1);
  const [perPage, setPerPageState] = useState(DEFAULT_PER_PAGE);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState<string | null>(null);
  const [filters, setFilters] = useState<BulkEditInputsFilters>(() => {
    return {
      keyword: '',
      versionId: null,
      classificationType: null,
      isSiengeBase: false,
      unitMeasureId: null,
    };
  });
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [editedValues, setEditedValues] = useState<EditedValues>({});

  const [isSaving, setIsSaving] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  const requestIdRef = useRef(0);

  const loadData = useCallback(async (): Promise<void> => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setStatus('loading');
    setLoadErrorMessage(null);

    try {
      const normalizedKeyword = filters.keyword.trim();
      let classificationTypeFilter = {};

      if (filters.classificationType) {
        if (filters.isSiengeBase) {
          classificationTypeFilter = {
            'filter[classification_external_type]': filters.classificationType,
          };
        } else {
          classificationTypeFilter = {
            'filter[classification_type]': filters.classificationType,
          };
        }
      }

      const response = await fetchBudgetInputPrices(budgetId, {
        page,
        per_page: perPage,
        ...(sort ? { sort } : {}),
        ...(normalizedKeyword ? { 'filter[keyword]': normalizedKeyword } : {}),
        ...(filters.versionId
          ? { 'filter[version_id]': filters.versionId }
          : {}),
        ...classificationTypeFilter,
        ...(filters.unitMeasureId
          ? { 'filter[unit_measure_id]': filters.unitMeasureId }
          : {}),
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      const responseData = response.data || [];
      const nextLastPage = response.meta?.last_page || response.last_page || 1;
      const nextTotal =
        response.meta?.total ||
        response.total ||
        (Array.isArray(responseData) ? responseData.length : 0);

      setInputs(responseData);
      setLastPage(nextLastPage);
      setTotal(nextTotal);
      setStatus(responseData.length > 0 ? 'success' : 'empty');
      setSelectedIds((previousSelected) => {
        const availableIds = new Set(responseData.map((item) => item.id));
        return previousSelected.filter((id) => availableIds.has(id));
      });
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setInputs([]);
      setLastPage(1);
      setTotal(0);
      setStatus('error');
      setLoadErrorMessage(
        getErrorMessage(
          error,
          'Houve um erro ao buscar os insumos do orçamento.',
        ),
      );
    }
  }, [
    budgetId,
    filters.classificationType,
    filters.isSiengeBase,
    filters.keyword,
    filters.unitMeasureId,
    filters.versionId,
    page,
    perPage,
    sort,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (page > lastPage) {
      setPageState(1);
    }
  }, [page, lastPage]);

  const modifiedIds = useMemo(
    () => getModifiedIds(editedValues),
    [editedValues],
  );

  const invalidModifiedIds = useMemo(() => {
    return getInvalidEditedIds(editedValues);
  }, [editedValues]);

  const hasUnsavedChanges = modifiedIds.length > 0;
  const hasValidChanges = hasUnsavedChanges && invalidModifiedIds.length === 0;

  const setPage = useCallback((nextPage: number) => {
    setPageState(nextPage > 0 ? nextPage : 1);
  }, []);

  const setPerPage = useCallback((nextPerPage: number) => {
    setPerPageState(nextPerPage);
    setPageState(1);
  }, []);

  const updateSort = useCallback((nextSort: string) => {
    const normalizedSort = nextSort.trim();

    setSort(normalizedSort || null);
    setPageState(1);
  }, []);

  const applyFilters = useCallback((nextFilters: BulkEditInputsFilters) => {
    const normalizedFilters: BulkEditInputsFilters = {
      keyword: nextFilters.keyword.trim(),
      versionId: nextFilters.versionId,
      classificationType: nextFilters.classificationType,
      isSiengeBase: nextFilters.isSiengeBase,
      unitMeasureId: nextFilters.unitMeasureId,
    };

    setFilters(normalizedFilters);
    setPageState(1);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const toggleSelection = useCallback((id: number) => {
    setSelectedIds((previousSelected) => {
      if (previousSelected.includes(id)) {
        return previousSelected.filter((currentId) => currentId !== id);
      }

      return [...previousSelected, id];
    });
  }, []);

  const toggleSelectAllCurrentPage = useCallback(() => {
    const currentPageIds = inputs.map((item) => item.id);
    const currentPageIdsSet = new Set(currentPageIds);

    setSelectedIds((previousSelected) => {
      const previousSelectedSet = new Set(previousSelected);
      const isAllSelected =
        currentPageIds.length > 0 &&
        currentPageIds.every((id) => previousSelectedSet.has(id));

      if (isAllSelected) {
        return previousSelected.filter((id) => !currentPageIdsSet.has(id));
      }

      const merged = [...previousSelected];

      currentPageIds.forEach((id) => {
        if (!previousSelectedSet.has(id)) {
          merged.push(id);
        }
      });

      return merged;
    });
  }, [inputs]);

  const editUnitPrice = useCallback(
    (item: BudgetInputPrice, value: number | null | undefined) => {
      setEditedValues((previousEditedValues) => {
        return setEditedValue(previousEditedValues, item, value);
      });
      setSaveErrorMessage(null);
    },
    [],
  );

  const undoItemChanges = useCallback((id: number) => {
    setEditedValues((previousEditedValues) => {
      return clearEditedValuesForIds(previousEditedValues, [id]);
    });
    setSaveErrorMessage(null);
  }, []);

  const undoSelectedChanges = useCallback(() => {
    setEditedValues((previousEditedValues) => {
      return clearEditedValuesForIds(previousEditedValues, selectedIds);
    });
    setSaveErrorMessage(null);
  }, [selectedIds]);

  const applyFixedPriceToSelection = useCallback(
    (value: number | null | undefined) => {
      setEditedValues((previousEditedValues) => {
        return applyFixedPriceToIds(
          previousEditedValues,
          inputs,
          selectedIds,
          value,
        );
      });
      setSaveErrorMessage(null);
    },
    [inputs, selectedIds],
  );

  const applyPercentageToSelection = useCallback(
    (percentage: number | null | undefined) => {
      setEditedValues((previousEditedValues) => {
        return applyPercentageToIds(
          previousEditedValues,
          inputs,
          selectedIds,
          percentage,
        );
      });
      setSaveErrorMessage(null);
    },
    [inputs, selectedIds],
  );

  const saveChanges = useCallback(async (): Promise<boolean> => {
    if (isSaving) {
      return false;
    }

    const invalidIds = getInvalidEditedIds(editedValues);

    if (invalidIds.length > 0) {
      setSaveErrorMessage(
        'Existem valores inválidos. Informe valores unitários maiores que zero.',
      );
      return false;
    }

    const payload = buildMassUpdatePayload(editedValues);

    if (payload.length === 0) {
      return false;
    }

    setIsSaving(true);
    setSaveErrorMessage(null);

    try {
      await massUpdateBudgetInputPrices(budgetId, payload);

      setEditedValues({});
      setSelectedIds([]);

      return true;
    } catch (error) {
      setSaveErrorMessage(
        getErrorMessage(error, 'Houve um erro ao salvar os insumos alterados.'),
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [budgetId, editedValues, isSaving]);

  return {
    status,
    inputs,
    page,
    perPage,
    lastPage,
    total,
    sort,
    selectedIds,
    editedValues,
    modifiedIds,
    invalidModifiedIds,
    hasUnsavedChanges,
    hasValidChanges,
    isSaving,
    loadErrorMessage,
    saveErrorMessage,
    setPage,
    setPerPage,
    updateSort,
    applyFilters,
    reload: loadData,
    toggleSelection,
    toggleSelectAllCurrentPage,
    clearSelection,
    editUnitPrice,
    undoItemChanges,
    undoSelectedChanges,
    applyFixedPriceToSelection,
    applyPercentageToSelection,
    saveChanges,
  };
};

export default useBulkEditInputs;
